/*
  VIOLA — Discount popup ("scratch to reveal")
  ------------------------------------------------------------------
  Replaces the earlier Plinko ball-drop popup with a simpler flow: a
  scratch card reveals a "Mystery Prize" (the real 10% reward stays
  withheld), then email capture (still framed as a mystery prize), then
  the real percentage + code render directly on screen together (never
  "check your email").

  Email capture is a real {% form 'customer' %} in discount-popup.liquid,
  submitted natively (not via fetch) — a fetch-based hand POST was tried
  first and got hard-rejected by Shopify's spam protection with "Missing
  CAPTCHA token" on every single real submission, since that token is only
  ever attached by Shopify's own script watching a form it rendered submit
  for real. That means a valid email navigates the page away; the popup
  then resumes itself on the very next load, gated on a one-shot
  sessionStorage "submitting" flag set right before the native submit and
  reading success straight off the ?customer_posted=true query param
  Shopify's own redirect adds — NOT Liquid's form.posted_successfully?,
  which tested unreliable live (read false on a submission that
  concretely succeeded) — see openClaimed() / openEmailError() and the
  boot logic at the bottom of this file.

    StorageManager        sessionStorage/localStorage state
    DiscountManager        confirms the fixed reward has a code configured
    EmailCaptureManager     validates the email client-side (format only —
                             the actual submission is a real native <form>
                             post, not JS-driven, see above)
    ScratchCard              canvas foil layer the visitor drags to erase,
                              revealing the DOM reward text underneath
    AccessibilityManager     focus trap, ESC, focus restore, aria-live
    PopupController           open/close lifecycle, scroll lock, timers
    DiscountModal              wires the above to the DOM + user events,
                                owns the ready -> revealing -> email_required
                                -> submitting -> claimed state machine, plus
                                the openClaimed()/openEmailError() resume
                                paths for the post-redirect page load
*/
(function () {
  "use strict";

  var ROOT_ID = "viola-discount-root";
  var root = document.getElementById(ROOT_ID);
  if (!root) return;

  var config;
  try {
    config = JSON.parse(document.getElementById("viola-discount-config").textContent);
  } catch (e) {
    return; // no config, nothing to do
  }

  var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ------------------------------------------------------------ */
  /* StorageManager                                                */
  /* ------------------------------------------------------------ */
  var StorageManager = {
    KEYS: {
      seen: "viola_discount_seen",
      played: "viola_discount_played",
      reward: "viola_discount_reward",
      code: "viola_discount_code",
      // Set the instant the card is revealed and only cleared once the
      // email is successfully submitted, so a refresh/reopen mid-flow
      // resumes into the email step instead of starting over.
      pending: "viola_discount_pending",
      emailSubmitted: "viola_discount_email_submitted",
      // One-shot flag set right before letting a valid email's native
      // form submission through (see the emailForm "submit" listener) so
      // the very next page load — the redirect back — knows to check for
      // a resume, instead of every ordinary page load bothering to parse
      // the URL. tab-scoped (sessionStorage) on purpose, not persisted.
      submitting: "viola_discount_submitting",
      // Persistent "Scratch to Win" side tab (see TabController below).
      // tabShown: set once the shopper closes the popup without
      // completing it — persisted (localStorage) so the tab keeps
      // showing on every later page until claimed, not just the tab that
      // was open when they closed it. tabDismissed: set when they
      // dismiss the TAB's own × — session-scoped on purpose (a quieter,
      // temporary "not now" rather than a permanent opt-out; it comes
      // back on a fresh visit, matching StorageManager.KEYS.seen's
      // session-only precedent above).
      tabShown: "viola_discount_tab_shown",
      tabDismissed: "viola_discount_tab_dismissed"
    },
    get: function (key, storage) {
      try {
        return (storage || window.sessionStorage).getItem(key);
      } catch (e) {
        return null;
      }
    },
    set: function (key, value, storage) {
      try {
        (storage || window.sessionStorage).setItem(key, value);
      } catch (e) {
        /* storage unavailable (private mode etc.) — fail silently, feature degrades gracefully */
      }
    },
    remove: function (key, storage) {
      try {
        (storage || window.sessionStorage).removeItem(key);
      } catch (e) {}
    },
    hasSeenThisSession: function () {
      return this.get(this.KEYS.seen, window.sessionStorage) === "1";
    },
    markSeenThisSession: function () {
      this.set(this.KEYS.seen, "1", window.sessionStorage);
    },
    /* "played" here means fully claimed — card revealed AND email submitted. */
    hasPlayed: function () {
      return this.get(this.KEYS.played, window.localStorage) === "1";
    },
    markPlayed: function (reward) {
      this.set(this.KEYS.played, "1", window.localStorage);
      this.set(this.KEYS.reward, String(reward.percentage), window.localStorage);
      this.set(this.KEYS.code, reward.code, window.localStorage);
      this.set(this.KEYS.emailSubmitted, "1", window.localStorage);
    },
    setPending: function (reward) {
      try {
        this.set(this.KEYS.pending, JSON.stringify(reward), window.localStorage);
      } catch (e) {}
    },
    getPending: function () {
      var raw = this.get(this.KEYS.pending, window.localStorage);
      if (!raw) return null;
      try {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.percentage && parsed.code) return parsed;
      } catch (e) {}
      return null;
    },
    clearPending: function () {
      this.remove(this.KEYS.pending, window.localStorage);
    },
    markTabShown: function () {
      this.set(this.KEYS.tabShown, "1", window.localStorage);
    },
    shouldShowTab: function () {
      return this.get(this.KEYS.tabShown, window.localStorage) === "1";
    },
    dismissTabThisSession: function () {
      this.set(this.KEYS.tabDismissed, "1", window.sessionStorage);
    },
    isTabDismissedThisSession: function () {
      return this.get(this.KEYS.tabDismissed, window.sessionStorage) === "1";
    }
  };

  /* ------------------------------------------------------------ */
  /* DiscountManager — the frontend is never the source of truth.  */
  /* It only confirms the fixed reward has a code configured;      */
  /* Shopify itself validates the code at checkout.                */
  /* ------------------------------------------------------------ */
  var DiscountManager = {
    validate: function (reward) {
      return !!(reward && reward.code && reward.percentage);
    }
  };

  /* ------------------------------------------------------------ */
  /* EmailCaptureManager — the discount code never renders until    */
  /* the real {% form 'customer' %} in discount-popup.liquid           */
  /* actually submits and the ?customer_posted=true redirect resume     */
  /* (see the boot logic below) confirms it. Submits through the same    */
  /* Shopify customer-creation endpoint the footer newsletter form       */
  /* uses, so the email becomes a real Shopify Customer record —         */
  /* tagged per campaign — rather than only living in localStorage.      */
  /* localStorage only ever holds a boolean flag (see StorageManager.    */
  /* KEYS.emailSubmitted), never the email.                                */
  /*                                                                       */
  /* Only the client-side email-format check lives here now — actually    */
  /* POSTing the form is intentionally NOT done from JS (no fetch, no      */
  /* XHR): Shopify's own spam protection rejects a hand-posted /contact    */
  /* request with a hard "Missing CAPTCHA token" 400, because that token   */
  /* only ever gets attached by Shopify's own script watching a form it    */
  /* rendered submit *natively*. A valid email is deliberately left to     */
  /* navigate away for real; see the emailForm "submit" listener below.    */
  /* ------------------------------------------------------------ */
  var EmailCaptureManager = {
    EMAIL_RE: /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/,
    isValidEmail: function (value) {
      return !!value && this.EMAIL_RE.test(value);
    }
  };

  /* ------------------------------------------------------------ */
  /* ScratchCard — a canvas foil layer painted over the DOM reward   */
  /* text. Dragging (pointer or touch) erases pixels via              */
  /* destination-out; a low-res sample canvas tracks cleared %        */
  /* cheaply without scanning the full-resolution canvas every move.  */
  /* A visible "Reveal my prize" button is the keyboard/           */
  /* screen-reader equivalent — it completes the reveal instantly.    */
  /* ------------------------------------------------------------ */
  var SCRATCH_RADIUS = 22; // px, at canvas-native resolution
  var SCRATCH_THRESHOLD = 0.55; // fraction cleared before auto-reveal — bumped up again per explicit request; history: 0.65 -> 0.3 -> 0.45 -> 0.55
  var SAMPLE_W = 40;
  var SAMPLE_H = 20;

  function ScratchCard(wrapEl, canvasEl) {
    this.wrap = wrapEl;
    this.canvas = canvasEl;
    this.ctx = canvasEl.getContext("2d");
    this.sample = document.createElement("canvas");
    this.sample.width = SAMPLE_W;
    this.sample.height = SAMPLE_H;
    this.sampleCtx = this.sample.getContext("2d");
    this._revealed = false;
    this._scratching = false;
    this._lastPoint = null;
    this._onResolve = null;
    this._queue = [];
    this._raf = 0;
    this._grid = new Uint8Array(SAMPLE_W * SAMPLE_H);
    this._cleared = 0;
    this._bind();

    // 2026-09-14: the foil is now the exact scratch-card artwork supplied
    // (assets/discount-popup-scratch.png, referenced via the canvas's own
    // data-foil-src so this file stays asset-path-agnostic) instead of a
    // procedurally-drawn gradient/texture. Loaded once here and cached on
    // the instance; paint() re-draws from this same Image every time
    // (resize, reset) once it's loaded — no re-fetch. Until it loads,
    // paint() falls back to a plain flat gold fill so the card never
    // shows as blank/broken on a slow connection.
    this._foilSrc = canvasEl.getAttribute("data-foil-src") || "";
    this._foilImg = null;
    this._foilReady = false;
    if (this._foilSrc) {
      var self = this;
      var img = new Image();
      img.onload = function () {
        self._foilReady = true;
        self.paint();
      };
      img.src = this._foilSrc;
      this._foilImg = img;
    }
  }

  ScratchCard.prototype._bind = function () {
    var self = this;
    this.canvas.addEventListener("pointerdown", function (e) { self._start(e); });
    this.canvas.addEventListener("pointermove", function (e) { self._move(e); });
    this.canvas.addEventListener("pointerup", function () { self._end(); });
    this.canvas.addEventListener("pointercancel", function () { self._end(); });
    // Fallback in case capture (below) isn't supported: a drag that ends
    // outside the small card's bounds still needs to stop cleanly.
    window.addEventListener("pointerup", function () { self._end(); });
    window.addEventListener("pointercancel", function () { self._end(); });
  };

  /** Sizes the canvas to the wrap's real rendered box and paints the foil. */
  ScratchCard.prototype.paint = function () {
    var rect = this.wrap.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = Math.max(1, Math.round(rect.width));
    this.h = Math.max(1, Math.round(rect.height));
    this.canvas.width = this.w * dpr;
    this.canvas.height = this.h * dpr;
    this._grid = new Uint8Array(SAMPLE_W * SAMPLE_H);
    this._cleared = 0;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.ctx.globalCompositeOperation = "source-over";

    // 2026-09-14: solid flat placeholder fill, no gradient/texture/marks
    // of its own — just a single flat tone so the real reward text
    // underneath can NEVER flash into view for an instant before the
    // real foil image (below) has finished loading (a real bug: with no
    // fill at all here, a slow first load left the canvas fully
    // transparent for a moment, showing "You've got 10% off" right
    // through it). This is NOT the earlier two-tone gradient/fallback
    // that caused the "2 scratch backgrounds" complaint — it's one flat
    // color, and the real image (cropped to its own opaque bounds, see
    // the constructor) fully covers it the instant it's ready, so
    // nothing ever visibly peeks out from behind it once loaded.
    this.ctx.fillStyle = "#d9a53f";
    this.ctx.fillRect(0, 0, this.w, this.h);

    if (this._foilReady && this._foilImg) {
      // "cover" draw (crop to fill, no stretch/distortion) — the source
      // PNG's own aspect ratio (1774x887) is close to but not exactly
      // the card's (now taller than 2.1:1, see .viola-discount__scratch-
      // wrap's aspect-ratio), so a plain stretched drawImage would
      // visibly squash it slightly.
      var imgW = this._foilImg.naturalWidth;
      var imgH = this._foilImg.naturalHeight;
      // 2026-09-14: +2% overscan on top of the exact cover scale — a
      // visitor reported a thin horizontal "bar" of the flat placeholder
      // fill above peeking through right at the top/bottom edge. Root
      // cause: this.w/this.h are Math.round()ed from a fractional
      // getBoundingClientRect(), so the "exact" cover scale could
      // under-cover the real (unrounded) box by a sub-pixel, leaving a
      // hairline gap. A small deliberate overscan guarantees the image
      // always fully covers the box regardless of that rounding, so the
      // placeholder fill never shows through once the image is ready.
      var scale = Math.max(this.w / imgW, this.h / imgH) * 1.02;
      var drawW = imgW * scale;
      var drawH = imgH * scale;
      var dx = (this.w - drawW) / 2;
      var dy = (this.h - drawH) / 2;
      this.ctx.drawImage(this._foilImg, dx, dy, drawW, drawH);
    }
  };

  ScratchCard.prototype.reset = function () {
    this._revealed = false;
    this._scratching = false;
    this._lastPoint = null;
    this._queue = [];
    this._grid = new Uint8Array(SAMPLE_W * SAMPLE_H);
    this._cleared = 0;
    this.canvas.classList.remove("is-clearing");
    this.canvas.style.opacity = "";
    this.wrap.classList.remove("has-started", "is-revealed");
    this.paint();
  };

  ScratchCard.prototype._toLocal = function (e) {
    var rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  ScratchCard.prototype._start = function (e) {
    if (this._revealed) return;
    e.preventDefault();
    // Pointer capture keeps pointermove/pointerup firing on the canvas
    // even once a fast drag leaves its (small) bounds, rather than
    // silently stopping mid-scratch.
    if (this.canvas.setPointerCapture) {
      try { this.canvas.setPointerCapture(e.pointerId); } catch (err) {}
    }
    this._scratching = true;
    this.wrap.classList.add("has-started");
    this._lastPoint = this._toLocal(e);
    this._queue = [this._lastPoint];
    this._schedule();
  };

  ScratchCard.prototype._move = function (e) {
    if (!this._scratching || this._revealed) return;
    e.preventDefault();
    // Phones report touch moves faster than the screen refreshes — take
    // every coalesced sample so the stroke stays smooth, but only queue
    // them here; all drawing happens once per frame in _flush.
    var evs = e.getCoalescedEvents ? e.getCoalescedEvents() : null;
    if (!evs || !evs.length) evs = [e];
    for (var i = 0; i < evs.length; i++) this._queue.push(this._toLocal(evs[i]));
    this._schedule();
  };

  ScratchCard.prototype._end = function () {
    if (!this._scratching) return;
    this._scratching = false;
    this._flush();
    this._checkProgress();
  };

  ScratchCard.prototype._schedule = function () {
    if (this._raf) return;
    var self = this;
    this._raf = requestAnimationFrame(function () {
      self._raf = 0;
      self._flush();
    });
  };

  /** Draws every queued point as ONE round-capped stroke, then marks the coverage grid. */
  ScratchCard.prototype._flush = function () {
    var q = this._queue;
    if (!q || !q.length || this._revealed) return;
    var ctx = this.ctx;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = SCRATCH_RADIUS * 2;
    ctx.beginPath();
    var prev = this._lastPoint || q[0];
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(prev.x, prev.y); // a tap alone still leaves a round dot
    for (var i = 0; i < q.length; i++) {
      ctx.lineTo(q[i].x, q[i].y);
      this._mark(prev, q[i]);
      prev = q[i];
    }
    ctx.stroke();
    this._lastPoint = prev;
    this._queue = [];
    this._checkProgress();
  };

  /** Marks the coarse coverage grid along a segment — no pixel read-back needed. */
  ScratchCard.prototype._mark = function (from, to) {
    var cw = this.w / SAMPLE_W;
    var ch = this.h / SAMPLE_H;
    var dx = to.x - from.x;
    var dy = to.y - from.y;
    var steps = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dy * dy) / (SCRATCH_RADIUS * 0.6)));
    var r2 = SCRATCH_RADIUS * SCRATCH_RADIUS;
    for (var i = 0; i <= steps; i++) {
      var x = from.x + (dx * i) / steps;
      var y = from.y + (dy * i) / steps;
      var c0 = Math.max(0, Math.floor((x - SCRATCH_RADIUS) / cw));
      var c1 = Math.min(SAMPLE_W - 1, Math.floor((x + SCRATCH_RADIUS) / cw));
      var r0 = Math.max(0, Math.floor((y - SCRATCH_RADIUS) / ch));
      var r1 = Math.min(SAMPLE_H - 1, Math.floor((y + SCRATCH_RADIUS) / ch));
      for (var r = r0; r <= r1; r++) {
        for (var c = c0; c <= c1; c++) {
          var ex = (c + 0.5) * cw - x;
          var ey = (r + 0.5) * ch - y;
          if (ex * ex + ey * ey <= r2 && !this._grid[r * SAMPLE_W + c]) {
            this._grid[r * SAMPLE_W + c] = 1;
            this._cleared++;
          }
        }
      }
    }
  };

  ScratchCard.prototype._checkProgress = function () {
    if (this._revealed || !this.w || !this.h) return;
    if (this._cleared / (SAMPLE_W * SAMPLE_H) >= SCRATCH_THRESHOLD) {
      this.reveal();
    }
  };

  /** Completes the reveal — via threshold, the fallback button, or reduced motion. */
  ScratchCard.prototype.reveal = function () {
    if (this._revealed) return;
    this._revealed = true;
    this._scratching = false;
    this.wrap.classList.add("is-revealed");
    this.canvas.classList.add("is-clearing");
    var self = this;
    var onDone = function () {
      if (self._onResolve) {
        var resolve = self._onResolve;
        self._onResolve = null;
        resolve();
      }
    };
    if (prefersReducedMotion) {
      onDone();
    } else {
      // Matches the canvas's own opacity transition duration (see
      // .viola-discount__scratch-canvas.is-clearing in discount-popup.css)
      // — chained off setTimeout rather than a transitionend listener so a
      // backgrounded tab can't stall it (the same rendering-pipeline
      // gating that affected WAAPI's `finished` promise in the old
      // Plinko popup applies to transitionend too).
      setTimeout(onDone, 420);
    }
  };

  /** Resolves once the card is fully revealed (threshold, button, or reduced motion). */
  ScratchCard.prototype.play = function () {
    var self = this;
    return new Promise(function (resolve) {
      self._onResolve = resolve;
    });
  };

  /* ------------------------------------------------------------ */
  /* AccessibilityManager — focus trap + ESC + focus restore        */
  /* ------------------------------------------------------------ */
  function AccessibilityManager(dialogEl) {
    this.dialogEl = dialogEl;
    this._lastFocused = null;
    this._onKeydown = this._onKeydown.bind(this);
  }

  AccessibilityManager.prototype.focusables = function () {
    return Array.prototype.slice.call(
      this.dialogEl.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  };

  AccessibilityManager.prototype.activate = function (onEscape) {
    this._lastFocused = document.activeElement;
    this._onEscape = onEscape;
    document.addEventListener("keydown", this._onKeydown, true);
    var first = this.focusables()[0];
    if (first) first.focus();
  };

  AccessibilityManager.prototype.deactivate = function () {
    document.removeEventListener("keydown", this._onKeydown, true);
    if (this._lastFocused && typeof this._lastFocused.focus === "function") {
      this._lastFocused.focus();
    }
    this._lastFocused = null;
  };

  AccessibilityManager.prototype._onKeydown = function (e) {
    if (e.key === "Escape") {
      e.preventDefault();
      if (this._onEscape) this._onEscape();
      return;
    }
    if (e.key !== "Tab") return;
    var items = this.focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------ */
  /* PopupController — scroll lock + body state, kept separate from */
  /* the modal's DOM/animation concerns.                             */
  /* ------------------------------------------------------------ */
  var PopupController = {
    _scrollY: 0,
    lockScroll: function () {
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = "fixed";
      document.body.style.top = "-" + this._scrollY + "px";
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    },
    unlockScroll: function () {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, this._scrollY);
    }
  };

  /* ------------------------------------------------------------ */
  /* Confetti — one-shot canvas burst fired the instant "Mystery    */
  /* Prize" becomes visible. Two cannons planted at the dialog's     */
  /* own bottom-left/bottom-right corners, each firing at a 45°       */
  /* angle up and inward (a slight random spread per particle, not   */
  /* a rigid line) so the two streams cross into a realistic V over   */
  /* the reveal text, then fall back down under gravity. No canvas-   */
  /* confetti dependency — small enough to hand-roll and keeps the    */
  /* popup free of a third-party script.                              */
  /* ------------------------------------------------------------ */
  var Confetti = {
    COLORS: ["#d4af6a", "#f3e6c8", "#8a6d3b", "#ffffff", "#c98a3e"],
    GRAVITY: 1600, // px/s^2
    DRAG: 0.994, // per-frame velocity damping (air resistance)
    PARTICLES_PER_CANNON: 42,
    MAX_LIFE_MS: 2600,

    /**
     * Fires the burst into `canvas`. Safe to call repeatedly — a second
     * call cancels the in-flight loop first via _active so bursts never
     * stack, though in practice this fires exactly once per popup open
     * (see DiscountModal._revealReward, the "email" phase entry).
     */
    burst: function (canvas) {
      if (!canvas) return;
      if (prefersReducedMotion) return; // decorative only — skip, same as the old Plinko trail

      if (canvas._confettiActive) canvas._confettiActive.cancelled = true;
      var session = { cancelled: false };
      canvas._confettiActive = session;

      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = canvas.getBoundingClientRect();
      var w = Math.max(1, rect.width);
      var h = Math.max(1, rect.height);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var particles = [];
      // Bottom-left cannon: fires up-and-right at 45° (-45° in canvas
      // coordinates, where +y is down). Bottom-right cannon: the mirror,
      // up-and-left. +-18° spread per particle keeps the "45° angle" read
      // as a real cannon rather than a single perfect line.
      [
        { x: 0, y: h, baseAngle: -45 },
        { x: w, y: h, baseAngle: -135 }
      ].forEach(function (cannon) {
        for (var i = 0; i < Confetti.PARTICLES_PER_CANNON; i++) {
          var angleDeg = cannon.baseAngle + (Math.random() * 36 - 18);
          var angleRad = (angleDeg * Math.PI) / 180;
          var speed = 620 + Math.random() * 420; // px/s
          particles.push({
            x: cannon.x,
            y: cannon.y,
            vx: Math.cos(angleRad) * speed,
            vy: Math.sin(angleRad) * speed,
            size: 6 + Math.random() * 6,
            shape: Math.random() < 0.5 ? "rect" : "circle",
            color: Confetti.COLORS[(Math.random() * Confetti.COLORS.length) | 0],
            rotation: Math.random() * Math.PI * 2,
            spin: (Math.random() * 2 - 1) * 8, // rad/s
            born: 0
          });
        }
      });

      var start = null;
      var last = null;

      function frame(now) {
        if (session.cancelled) return;
        if (start === null) { start = now; last = now; }
        // Capped so a throttled/backgrounded tab (rAF pauses while hidden,
        // per the Chrome quirk in project memory) doesn't produce one huge
        // catch-up jump when it resumes — worst case this frame just
        // advances the sim by 50ms instead of however long it was hidden.
        var dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        var elapsed = now - start;

        ctx.clearRect(0, 0, w, h);

        var alive = false;
        for (var i = 0; i < particles.length; i++) {
          var p = particles[i];
          var age = elapsed - p.born;
          if (age < 0) { alive = true; continue; } // not launched yet (all born at 0 here, kept for future stagger)
          if (age > Confetti.MAX_LIFE_MS) continue;

          p.vx *= Confetti.DRAG;
          p.vy = p.vy * Confetti.DRAG + Confetti.GRAVITY * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rotation += p.spin * dt;

          if (p.y - p.size > h) continue; // settled below the visible board, stop drawing it

          alive = true;
          var fade = age > Confetti.MAX_LIFE_MS - 500 ? Math.max(0, (Confetti.MAX_LIFE_MS - age) / 500) : 1;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.globalAlpha = fade;
          ctx.fillStyle = p.color;
          if (p.shape === "rect") {
            ctx.fillRect(-p.size / 2, -p.size / 3.2, p.size, p.size / 1.6);
          } else {
            ctx.beginPath();
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }

        if (alive) {
          requestAnimationFrame(frame);
        } else {
          ctx.clearRect(0, 0, w, h);
        }
      }

      requestAnimationFrame(frame);
    }
  };

  /* ------------------------------------------------------------ */
  /* DiscountModal — wires everything to the actual DOM in the      */
  /* snippet and to user interaction.                                */
  /* ------------------------------------------------------------ */
  function DiscountModal(rootEl, cfg) {
    this.root = rootEl;
    this.cfg = cfg;
    this.dialog = rootEl.querySelector(".viola-discount__dialog");
    this.backdrop = rootEl.querySelector(".viola-discount__backdrop");
    this.closeBtn = rootEl.querySelector(".viola-discount__close");
    this.scratchWrap = rootEl.querySelector(".viola-discount__scratch-wrap");
    this.scratchCanvas = rootEl.querySelector(".viola-discount__scratch-canvas");
    this.revealBtn = rootEl.querySelector(".viola-discount__reveal-btn");
    // Points at the value span inside .viola-discount__claimed-reveal
    // specifically (not the mystery block's static "Mystery Prize" text) —
    // the real percentage is only ever written here, in _claim(), after
    // the email submits.
    this.percentageEl = rootEl.querySelector(".viola-discount__claimed-percentage-value");
    // The code text lives in its own inner span — .viola-discount__code
    // itself also wraps a decorative copy-icon SVG, so writing text
    // straight into the outer element would silently delete that icon.
    this.codeEl = rootEl.querySelector(".viola-discount__code-value");
    this.copyBtn = rootEl.querySelector(".viola-discount__copy");
    this.shopBtn = rootEl.querySelector(".viola-discount__shop");
    this.continueBtn = rootEl.querySelector(".viola-discount__continue");
    this.retryBtn = rootEl.querySelector(".viola-discount__retry");
    this.liveRegion = rootEl.querySelector(".viola-discount__live-region");
    this.confettiCanvas = rootEl.querySelector(".viola-discount__confetti-canvas");

    this.emailPanel = rootEl.querySelector(".viola-discount__email-panel");
    this.emailForm = rootEl.querySelector(".viola-discount__email-form");
    this.emailInput = rootEl.querySelector(".viola-discount__email-input");
    this.emailError = rootEl.querySelector(".viola-discount__email-error");
    this.emailSubmitBtn = rootEl.querySelector(".viola-discount__email-submit");
    this.emailSubmitLabel = rootEl.querySelector(".viola-discount__email-submit-label");

    this.card = new ScratchCard(this.scratchWrap, this.scratchCanvas);
    this.a11y = new AccessibilityManager(this.dialog);
    // explicit state machine: ready -> revealing -> email_required ->
    // submitting -> claimed, with "error" reserved for an unrecoverable
    // config problem (see _showError). Every transition is guarded at
    // its entry point (reveal() / submitEmail()) rather than assumed.
    this.state = "ready";
    this.currentReward = null;
    this._closeTimer = null;
    this._emailSubmitLabelDefault = this.emailSubmitLabel ? this.emailSubmitLabel.textContent : "";

    this._bind();
  }

  DiscountModal.prototype._bind = function () {
    var self = this;
    this.closeBtn.addEventListener("click", function () { self.close(); });
    this.backdrop.addEventListener("click", function () { self.close(); });
    // 2026-09-14: "Reveal my prize" button removed from the markup per
    // explicit request (it was the keyboard/screen-reader-only fallback
    // for completing the reveal without a scratch drag — the scratch
    // gesture itself is now the only way in). Guarded since this.revealBtn
    // is null now that the element no longer exists.
    if (this.revealBtn) {
      this.revealBtn.addEventListener("click", function () { self.card.reveal(); });
    }
    this.copyBtn.addEventListener("click", function () { self.copyCode(); });
    if (this.shopBtn) {
      this.shopBtn.addEventListener("click", function () { self.close(); });
    }
    if (this.continueBtn) {
      this.continueBtn.addEventListener("click", function () { self.close(); });
    }
    if (this.retryBtn) {
      this.retryBtn.addEventListener("click", function () { self._resetToIntro(); });
    }
    if (this.emailForm) {
      // Unlike the old hand-posted-via-fetch version, this is a REAL
      // {% form 'customer' %} and a real native submission is exactly
      // what's needed now (see discount-popup.liquid) — Shopify's own
      // spam-protection script only attaches a valid CAPTCHA token to a
      // form it rendered actually submitting natively; faking the POST
      // via fetch got a hard "Missing CAPTCHA token" 400 every time. So
      // this listener only ever preventDefault()s when the email fails
      // client-side validation; a valid email is deliberately let through
      // to navigate away — the popup resumes on the next page load, see
      // the boot logic at the bottom of this file.
      this.emailForm.addEventListener("submit", function (e) {
        var email = self.emailInput ? self.emailInput.value.trim() : "";
        if (!EmailCaptureManager.isValidEmail(email)) {
          e.preventDefault();
          self.state = "email_error";
          self._showEmailError(self.cfg.copy.invalidEmail);
          return;
        }
        self.state = "submitting";
        self.emailPanel.classList.remove("has-error");
        if (self.emailSubmitBtn) self.emailSubmitBtn.disabled = true;
        if (self.emailSubmitLabel) self.emailSubmitLabel.textContent = self.cfg.copy.claiming;
        StorageManager.set(StorageManager.KEYS.submitting, "1", window.sessionStorage);
        // No preventDefault — the browser proceeds with the real POST.
      });
    }
    if (this.emailInput) {
      this.emailInput.addEventListener("input", function () {
        self.emailPanel.classList.remove("has-error");
        self.emailInput.removeAttribute("aria-invalid");
      });
    }
  };

  DiscountModal.prototype.open = function () {
    if (this.root.classList.contains("is-active")) return;
    this.root.classList.remove("is-revealed", "is-claimed-revealed");
    clearTimeout(this._revealDelayTimer);
    this.card.reset();

    // A reward already revealed in a previous visit/tab (but never
    // claimed) resumes straight into the email step instead of starting
    // over — see StorageManager.KEYS.pending.
    var pending = StorageManager.getPending();
    if (pending) {
      this.currentReward = pending;
      this._revealReward(pending);
    } else {
      this.root.setAttribute("data-phase", "intro");
      this.state = "ready";
      this._armCard();
    }

    this.root.setAttribute("aria-hidden", "false");
    this.root.classList.add("is-active");
    PopupController.lockScroll();
    // next frame so the transition actually runs, and so the scratch
    // canvas measures its real (now-visible) box before painting.
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add("is-open");
        self.card.paint();
      });
    });
    this.a11y.activate(function () { self.close(); });
    StorageManager.markSeenThisSession();
  };

  /**
   * Resume path for the page load right after a real native /contact
   * submission redirects back here *successfully* — detected via the
   * ?customer_posted=true query param (see the boot logic at the bottom
   * of this file). Skips scratch and email entirely — opens straight
   * into the claimed state, since the reward was already revealed and
   * the email already genuinely submitted before this page ever loaded.
   * Never shows a scratch card the visitor would have to play twice.
   */
  DiscountModal.prototype.openClaimed = function (reward) {
    if (this.root.classList.contains("is-active")) return;
    this.root.classList.remove("is-revealed", "is-claimed-revealed");
    this.currentReward = reward;
    this.root.setAttribute("aria-hidden", "false");
    this.root.classList.add("is-active");
    PopupController.lockScroll();
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add("is-open");
      });
    });
    this.a11y.activate(function () { self.close(); });
    StorageManager.markSeenThisSession();
    this._claim(reward);
  };

  /**
   * Resume path for the page load right after a real native submission
   * redirects back here WITHOUT the ?customer_posted=true success marker
   * — e.g. Shopify's spam check rejected it, or anything else went
   * wrong server-side. Reopens straight into the email step (reward
   * stays pending, not cleared) showing the same graceful inline error
   * the invalid-email path already has, so the visitor can just retry
   * instead of losing their revealed prize and restarting the scratch.
   */
  DiscountModal.prototype.openEmailError = function (reward) {
    if (this.root.classList.contains("is-active")) return;
    this.root.classList.remove("is-revealed", "is-claimed-revealed");
    this.currentReward = reward;
    this._revealReward(reward);
    this.root.setAttribute("aria-hidden", "false");
    this.root.classList.add("is-active");
    PopupController.lockScroll();
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add("is-open");
      });
    });
    this.a11y.activate(function () { self.close(); });
    StorageManager.markSeenThisSession();
    this.state = "email_error";
    this._showEmailError(this.cfg.copy.submitError);
  };

  DiscountModal.prototype.close = function () {
    if (!this.root.classList.contains("is-active")) return;
    var self = this;
    this.root.classList.add("is-closing");
    this.root.classList.remove("is-open");
    this.a11y.deactivate();
    // 2026-09-14: closing the popup for any reason (× button, backdrop
    // click, or the "Shop now"/"Continue shopping" buttons post-claim)
    // WITHOUT having actually completed the claim shows the persistent
    // side tab — see TabController below. Guarded on hasPlayed(), not on
    // *why* close() was called, so this naturally covers every dismissal
    // path with one check: a real claim always calls StorageManager.
    // markPlayed() before its own close() fires (see _claim()), so
    // hasPlayed() is already true by the time shopBtn/continueBtn's
    // close() runs and the tab correctly never shows for a completed claim.
    if (!StorageManager.hasPlayed()) {
      TabController.show();
    }
    clearTimeout(this._closeTimer);
    clearTimeout(this._revealDelayTimer);
    this._closeTimer = setTimeout(
      function () {
        self.root.classList.remove("is-active", "is-closing", "is-revealed", "is-claimed-revealed");
        self.root.setAttribute("aria-hidden", "true");
        if (self.state === "ready") {
          self.card.reset();
        }
        if (self.emailPanel) self.emailPanel.classList.remove("has-error");
        PopupController.unlockScroll();
      },
      prefersReducedMotion ? 10 : 480
    );
  };

  DiscountModal.prototype._resetToIntro = function () {
    this.root.setAttribute("data-phase", "intro");
    this.state = "ready";
    this.card.reset();
    this._armCard();
  };

  /**
   * Arms the promise that ScratchCard.reveal() resolves — called once per
   * fresh scratch card (on open, and on retry-from-error) so that BOTH
   * completion paths (dragging past the clear threshold, or clicking the
   * "Reveal my prize" fallback button) land here. Fixed reward (see
   * the config script tag in discount-popup.liquid) — there's no random
   * pick, the scratch card only gates *when* it's shown, not *what*.
   */
  DiscountModal.prototype._armCard = function () {
    if (this.state !== "ready") return; // guards against a second arm / any out-of-order call
    this.state = "revealing";

    var self = this;
    this.card.play().then(function () {
      var reward = self.cfg.reward;

      if (!DiscountManager.validate(reward)) {
        self._showError();
        return;
      }

      // Persisted the instant the card clears — before the reward panel
      // even renders — so a refresh or an early close right after
      // revealing still resumes into the email step instead of resetting.
      StorageManager.setPending(reward);
      self.currentReward = reward;

      // "Mystery Prize" is already sitting revealed on the scratched card
      // at this point — hold there for a beat before sliding into the
      // email-capture screen, instead of swapping views instantly.
      // Chained off setTimeout (not rAF/anim.finished) so the pause stays
      // exactly this long even if the tab gets backgrounded mid-pause; see
      // the Chrome rendering-gate quirk documented in project memory.
      // Trimmed from 2000ms per feedback (hold felt a beat too long), then
      // halved again from 1900ms per feedback (still too long), then
      // trimmed again from 950ms per feedback (still too long).
      clearTimeout(self._revealDelayTimer);
      self._revealDelayTimer = setTimeout(
        function () {
          self._revealReward(reward);
        },
        prefersReducedMotion ? 10 : 350
      );
    });
  };

  /**
   * Shows the "Mystery Prize" framing and moves into the email-capture
   * phase. The real reward stays withheld here on purpose — neither the
   * percentage nor the code touch the DOM until a successful email
   * submission lands in _claim(). `reward` is still threaded through
   * (validated, persisted as pending) so it's ready the instant email
   * capture succeeds.
   */
  DiscountModal.prototype._revealReward = function (reward) {
    var self = this;
    this.root.setAttribute("data-phase", "email");
    this.state = "email_required";
    if (this.liveRegion) {
      this.liveRegion.textContent = "You won a mystery prize. Enter your email to find out what you won.";
    }
    Confetti.burst(this.confettiCanvas);
    requestAnimationFrame(function () {
      self.root.classList.add("is-revealed");
    });
  };

  DiscountModal.prototype._showEmailError = function (message) {
    if (this.emailError) this.emailError.textContent = message;
    if (this.emailPanel) this.emailPanel.classList.add("has-error");
    if (this.emailInput) this.emailInput.setAttribute("aria-invalid", "true");
    if (this.liveRegion) this.liveRegion.textContent = message;
  };

  /**
   * Reward + email both settled — this is the only place the real
   * percentage AND the code render, immediately (not "check your email").
   * Everything before this point only ever showed the mystery framing.
   */
  DiscountModal.prototype._claim = function (reward) {
    var self = this;
    if (this.percentageEl) this.percentageEl.textContent = reward.percentage + "%";
    this.codeEl.textContent = reward.code;
    this.root.setAttribute("data-phase", "claimed");
    this.state = "claimed";
    StorageManager.markPlayed(reward);
    StorageManager.clearPending();
    if (this.liveRegion) {
      this.liveRegion.textContent = "You won " + reward.percentage + "% off! Your code is " + reward.code + ".";
    }
    requestAnimationFrame(function () {
      self.root.classList.add("is-claimed-revealed");
    });
  };

  DiscountModal.prototype._showError = function () {
    this.root.setAttribute("data-phase", "error");
    this.state = "error";
    if (this.liveRegion) {
      this.liveRegion.textContent = "Sorry, something went wrong.";
    }
  };

  DiscountModal.prototype.copyCode = function () {
    var code = this.codeEl.textContent;
    var self = this;
    var done = function () {
      // Icon-only button now (no "Copy code" text button) — feedback is a
      // copy-icon -> checkmark swap via .is-copied (see CSS) plus an
      // aria-live announcement for screen readers, instead of the old
      // textContent swap.
      self.copyBtn.classList.add("is-copied");
      self.copyBtn.setAttribute("aria-label", self.cfg.copy.copied);
      if (self.liveRegion) self.liveRegion.textContent = self.cfg.copy.copied;
      clearTimeout(self._copyResetTimer);
      self._copyResetTimer = setTimeout(function () {
        self.copyBtn.classList.remove("is-copied");
        self.copyBtn.setAttribute("aria-label", "Copy discount code");
      }, 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(function () {
        self._fallbackCopy(code, done);
      });
    } else {
      this._fallbackCopy(code, done);
    }
  };

  DiscountModal.prototype._fallbackCopy = function (code, done) {
    try {
      var textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      done();
    } catch (e) {
      /* clipboard truly unavailable — code is still visible on screen to copy by hand */
    }
  };

  /* ------------------------------------------------------------ */
  /* TabController — the persistent left-edge "Scratch to Win" tab. */
  /* Appears the instant the popup is closed without being claimed  */
  /* (see DiscountModal.prototype.close above), never appears at    */
  /* all once StorageManager.hasPlayed() is true, and stays gone    */
  /* for the rest of the browsing session once its own × is         */
  /* clicked (independent of the main popup). Markup/CSS live in    */
  /* discount-popup.liquid / discount-popup.css (#viola-discount-tab). */
  /* ------------------------------------------------------------ */
  var TabController = {
    el: document.getElementById("viola-discount-tab"),
    show: function () {
      if (!this.el) return;
      if (StorageManager.hasPlayed()) return;
      if (StorageManager.isTabDismissedThisSession()) return;
      StorageManager.markTabShown();
      this.el.hidden = false;
      this.el.setAttribute("aria-hidden", "false");
      // rAF so the hidden->visible change and the fade-in transition
      // (see .is-visible in discount-popup.css) don't collapse into one
      // un-animated frame.
      var self = this;
      requestAnimationFrame(function () {
        self.el.classList.add("is-visible");
      });
    },
    hide: function () {
      if (!this.el) return;
      this.el.classList.remove("is-visible");
      this.el.setAttribute("aria-hidden", "true");
      // Match the tab's own CSS fade duration (200ms, see .viola-
      // discount-tab transition) before actually hiding it from the a11y
      // tree / layout — an abrupt hidden=true mid-fade would cut the
      // animation short.
      var self = this;
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(function () {
        if (!self.el.classList.contains("is-visible")) self.el.hidden = true;
      }, prefersReducedMotion ? 10 : 220);
    },
    init: function () {
      if (!this.el) return;
      var self = this;
      var closeBtn = this.el.querySelector(".viola-discount-tab__close");
      var openBtn = this.el.querySelector(".viola-discount-tab__open");
      if (closeBtn) {
        closeBtn.addEventListener("click", function () {
          StorageManager.dismissTabThisSession();
          self.hide();
        });
      }
      if (openBtn) {
        openBtn.addEventListener("click", function () {
          self.hide();
          ensureModal().open();
        });
      }
      // Resume-visible on a later page load: hasPlayed() already fully
      // gates this (never shows post-claim), so the only thing to check
      // here is whether a PRIOR page in this browsing history already
      // earned the tab (StorageManager.shouldShowTab()) and it wasn't
      // dismissed since.
      if (StorageManager.shouldShowTab() && !StorageManager.hasPlayed() && !StorageManager.isTabDismissedThisSession()) {
        this.el.hidden = false;
        this.el.setAttribute("aria-hidden", "false");
        this.el.classList.add("is-visible");
      }
    }
  };
  TabController.init();

  /* ------------------------------------------------------------ */
  /* Eligibility + trigger timer                                    */
  /* ------------------------------------------------------------ */
  var modal = null;
  function ensureModal() {
    if (!modal) modal = new DiscountModal(root, config);
    return modal;
  }

  // Resume path: this page load is the redirect back from a real native
  // /contact submission (see the {% form 'customer' %} note in
  // discount-popup.liquid) — takes priority over the normal seen/played
  // gating below, since finishing that claim is the entire point of this
  // load. Gated on the one-shot "submitting" flag (set right before the
  // native submit) so an ordinary repeat visit with a stale pending
  // reward — or someone manually landing on a URL with a leftover
  // ?customer_posted param — doesn't fire this. Success is read straight
  // off the ?customer_posted=true query param Shopify's own redirect
  // adds, not Liquid's form.posted_successfully? — tested live and that
  // read false on a submission that concretely succeeded; see the
  // comment near the {% form %} tag in discount-popup.liquid.
  var wasSubmitting = StorageManager.get(StorageManager.KEYS.submitting, window.sessionStorage) === "1";
  if (wasSubmitting) {
    StorageManager.remove(StorageManager.KEYS.submitting, window.sessionStorage);
    var resumePending = StorageManager.getPending();
    if (resumePending) {
      var postedOk = false;
      try {
        postedOk = new URLSearchParams(window.location.search).get("customer_posted") === "true";
      } catch (e) {}
      if (postedOk) {
        ensureModal().openClaimed(resumePending);
      } else {
        ensureModal().openEmailError(resumePending);
      }
      // Tidy the ?customer_posted=true#DiscountEmailForm cruft off the
      // URL now that it's been read — cosmetic only (doesn't re-scroll
      // anything, the browser's own anchor-jump already happened before
      // this ran), never blocks the resume above if it fails for any
      // reason.
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch (e) {}
      return;
    }
  }

  if (StorageManager.hasPlayed() || StorageManager.hasSeenThisSession()) {
    return;
  }

  function isUserMidInteraction() {
    var active = document.activeElement;
    if (!active) return false;
    var tag = active.tagName;
    if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return true;
    if (active.closest && (active.closest("cart-drawer") || active.closest("[data-cart-drawer]"))) return true;
    return false;
  }

  var DELAY_MS = 6000; // was 4000 — pushed 2s later per feedback
  var RETRY_MS = 1200;
  var elapsedAtCheck = 0;

  function attemptOpen() {
    if (isUserMidInteraction() && elapsedAtCheck < DELAY_MS + 6000) {
      elapsedAtCheck += RETRY_MS;
      setTimeout(attemptOpen, RETRY_MS);
      return;
    }
    ensureModal().open();
  }

  var openTimer = setTimeout(attemptOpen, DELAY_MS);

  window.addEventListener("pagehide", function () {
    clearTimeout(openTimer);
  });
})();
